import { IFundingContractRepository, Total, TotalAmount, TotalSupporterCount } from '../domain/repositories';
import { FundingContractEntity } from '../domain/entities';
import { ContractId } from '../domain/value-objects';
import { ProductId } from '@services/MarppleShop/features/ProductMeta';
import { ItemOrderId } from '@services/MarppleShop/features/Product/domain';
import { AllPoolType } from '@services/MarppleShop/shared/database/db.ts';
import { Money } from '../../../../../shared/features/domain/value-objects/Money.ts';
import { FundingSupporter } from '@services/MarppleShop/features/Funding/domain/value-objects/funding-supporter.ts';
import { pipe, map, toArray } from '@fxts/core';

interface FundingOrderRow {
  id: number;
  product_id: number;
  user_id: number;
  item_order_id: number;
  amount: number;
  created_at: Date;
}

interface FundingSupporterRow {
  id: number;
  user_id: number;
  name: string;
  email: string;
  nick_name: string;
  profile_image: string | null;
  is_hidden: boolean;
  amount: number;
  created_at: Date;
}

export class FundingContractRepository implements IFundingContractRepository {
  constructor(private readonly db: AllPoolType) {}

  async findById(contractId: ContractId): Promise<FundingContractEntity | undefined> {
    const row = await this.db.QUERY1<FundingOrderRow>`
      SELECT
        fo.id,
        fo.product_id,
        fo.item_order_id,
        fo.user_id,
        fo.amount,
        fo.created_at
      FROM funding_orders fo
      WHERE fo.id = ${contractId.value}
    `;

    if (!row) return undefined;

    return this.mapToEntity(row);
  }

  async findByItemOrderId(itemOrderId: ItemOrderId): Promise<FundingContractEntity | undefined> {
    const row = await this.db.QUERY1<FundingOrderRow>`
      SELECT
        fo.id,
        fo.product_id,
        fo.item_order_id,
        fo.user_id,
        fo.amount,
        fo.created_at
      FROM funding_orders fo
      WHERE fo.item_order_id = ${itemOrderId.value}
    `;

    if (!row) return undefined;

    return this.mapToEntity(row);
  }

  async findByProductId(productId: ProductId): Promise<FundingContractEntity[]> {
    const rows = await this.db.QUERY<FundingOrderRow>`
      SELECT
        fo.id,
        fo.product_id,
        fo.item_order_id,
        fo.user_id,
        fo.amount,
        fo.created_at
      FROM funding_orders fo
      WHERE fo.product_id = ${productId.value}
    `;

    return rows.map((row) => this.mapToEntity(row));
  }

  async findByUserId(userId: number): Promise<FundingContractEntity[]> {
    const rows = await this.db.QUERY<FundingOrderRow>`
      SELECT
        fo.id,
        fo.product_id,
        fo.item_order_id,
        fo.user_id,
        fo.amount,
        fo.created_at
      FROM funding_orders fo
      WHERE fo.user_id = ${userId}
    `;

    return rows.map((row) => this.mapToEntity(row));
  }

  async save(contract: FundingContractEntity): Promise<void> {
    await this.db.QUERY1`
      INSERT INTO funding_orders ${this.db.VALUES({
        product_id: contract.productId.value,
        item_order_id: contract.itemOrderId.value,
        user_id: contract.userId,
        amount: contract.money.toKRW().getAmount(),
      })} 
    `;
  }

  async findSupporterRankings(
    productId: ProductId,
    createdAt: Date,
    money: Money,
    limit = 15,
  ): Promise<FundingSupporter[]> {
    const rows = await this.db.QUERY<FundingSupporterRow>`
      SELECT
        fo.user_id,
        u.name,
        u.email,
        u.nick_name,
        u.profile_image,
        u.is_hidden,
        SUM(fo.amount) as amount,
        MAX(fo.created_at) as created_at
      FROM funding_orders AS fo
        INNER JOIN users AS u ON u.id = fo.user_id
        INNER JOIN item_orders as io ON io.id = fo.item_order_id
      WHERE fo.product_id = ${productId.value}
        AND io.order_process_id = 'PURCHASE'
      GROUP BY fo.user_id, u.nick_name, u.profile_image, u.name, u.email, u.is_hidden
      ${
        money.isZero() || money.isNegative()
          ? this.db.SQL``
          : this.db.SQL`HAVING (
            SUM(fo.amount) < ${money.getAmount()} 
            OR (
              SUM(fo.amount) = ${money.getAmount()} 
              AND MAX(fo.created_at) < ${createdAt}
            )
          )`
      }
      ORDER BY amount DESC, created_at DESC
      LIMIT ${limit}
    `;

    return rows.map((row) =>
      FundingSupporter.of(
        row?.id,
        row.user_id,
        row.name,
        row.email,
        row.nick_name,
        row.profile_image,
        row.is_hidden,
        Number(row.amount),
        row.created_at,
      ),
    );
  }

  async findRecentSupporters(productId: ProductId, createdAt: Date, limit = 15): Promise<FundingSupporter[]> {
    const rows = await this.db.QUERY<FundingSupporterRow>`
      SELECT
        id,
        name,
        email,
        user_id,
        nick_name,
        profile_image,
        is_hidden,
        amount,
        created_at
      FROM (
        SELECT
          fo.id,
          fo.user_id,
          u.name,
          u.email,
          u.nick_name,
          u.profile_image,
          u.is_hidden,
          fo.amount,
          fo.created_at,
          ROW_NUMBER() OVER (
            PARTITION BY io.shipping_order_id
            ORDER BY fo.id DESC
            ) as rn
        FROM funding_orders AS fo
          INNER JOIN users AS u ON u.id = fo.user_id
          INNER JOIN item_orders AS io ON io.id = fo.item_order_id
        WHERE fo.product_id = ${productId.value}
          AND io.order_process_id = 'PURCHASE'
          AND fo.created_at < ${createdAt}
        ) ranked
      WHERE rn = 1
      ORDER BY created_at DESC
      LIMIT ${limit}
    `;

    return rows.map((row) =>
      FundingSupporter.of(
        row.id,
        row.user_id,
        row.name,
        row.email,
        row.nick_name,
        row.profile_image,
        row.is_hidden,
        Number(row.amount),
        row.created_at,
      ),
    );
  }

  async totalAmounts(productIds: ProductId[]): Promise<TotalAmount[]> {
    const rows = await this.db.QUERY<{ product_id: number; total_amount: number }>`
      SELECT
        fo.product_id,
        SUM(fo.amount) as total_amount
      FROM funding_orders as fo
          INNER JOIN item_orders as io ON io.id = fo.item_order_id
      WHERE io.order_process_id = 'PURCHASE'
        AND ${this.db.IN(
          'product_id',
          pipe(
            productIds,
            map((productId) => productId.value),
            toArray,
          ),
        )}
      GROUP BY fo.product_id
    `;

    return pipe(
      rows,
      map((row) => ({
        productId: new ProductId(row.product_id),
        totalAmount: Money.of(Number(row.total_amount)),
      })),
      toArray,
    );
  }

  async totalSupporterCounts(productIds: ProductId[]): Promise<TotalSupporterCount[]> {
    const rows = await this.db.QUERY<{ product_id: number; total_supporter_count: number }>`
      SELECT
        t.product_id,
        COUNT(DISTINCT user_id) as total_supporter_count
      FROM (
        SELECT
          fo.product_id,
          fo.user_id
        FROM funding_orders AS fo
            INNER JOIN item_orders as io ON io.id = fo.item_order_id
        WHERE io.order_process_id = 'PURCHASE'
          AND ${this.db.IN(
            'product_id',
            pipe(
              productIds,
              map((productId) => productId.value),
              toArray,
            ),
          )} 
        GROUP BY fo.product_id, fo.user_id
        ) t
      GROUP BY t.product_id;
    `;

    return pipe(
      rows,
      map((row) => ({
        productId: new ProductId(row.product_id),
        totalSupporterCount: Number(row.total_supporter_count),
      })),
      toArray,
    );
  }

  async total(productId: ProductId): Promise<Total> {
    const row = await this.db.QUERY1<{ total_supporter_count: number; total_amount: number }>`
      SELECT
        SUM(amount) as total_amount,
        COUNT(DISTINCT CASE WHEN user_net > 0 THEN user_id END) as total_supporter_count
      FROM (
        SELECT
          amount,
          user_id,
          SUM(amount) OVER (PARTITION BY user_id) as user_net
        FROM funding_orders
        WHERE product_id = ${productId.value}
        ) t;
    `;

    return {
      totalAmount: Money.of(Number(row?.total_amount)),
      totalSupporterCount: Number(row?.total_supporter_count),
    };
  }

  private mapToEntity(row: FundingOrderRow): FundingContractEntity {
    return FundingContractEntity.from({
      id: ContractId.of(row.id),
      productId: new ProductId(row.product_id),
      itemOrderId: ItemOrderId.of(row.item_order_id),
      userId: row.user_id,
      money: Money.of(row.amount),
      createdAt: new Date(row.created_at),
    });
  }
}
